import { concatPath } from '@feathr/hooks';
import type { Attributes, IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, DisplayModel } from '@feathr/rachis';

import type { Account } from './accounts';

export interface ILegacyAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface IBillable extends IBaseAttributes {
  name: string;
  email: string;
  description?: string;
  // TODO: Once data in DB is migrated to standard address format, use IAddress
  address?: ILegacyAddress;
  intacct: {
    contactname: string;
  };
}

export class Billable extends DisplayModel<IBillable> {
  public readonly className = 'Billable';

  public override collection: Billables<this> | null = null;

  public get constraints(): TConstraints<IBillable> {
    return {
      email: {
        presence: { allowEmpty: false },
        email: true,
      },
      name: {
        presence: { allowEmpty: false },
      },
      'address.line1': {
        presence: {
          allowEmpty: false,
          message: '^Address Line 1 must be set.',
        },
      },
      'address.city': {
        presence: {
          allowEmpty: false,
          message: '^City must be set.',
        },
      },
      'address.state': {
        presence: {
          allowEmpty: false,
          message: '^State must be set.',
        },
      },
      'address.postal_code': {
        presence: {
          allowEmpty: false,
          message: '^Postal Code must be set.',
        },
      },
      'address.country': {
        presence: {
          allowEmpty: false,
          message: '^Country must be set.',
        },
      },
    };
  }

  public override getDefaults(): Partial<IBillable> {
    return {
      integrations: [],
    };
  }

  public get name(): string {
    return this.get('name', '').trim() || 'Unnamed Billable';
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`settings/billing/configurations/${this.id}`, pathSuffix);
  }

  public isPrimary(account: Account): boolean {
    const license = account.get('license');
    const billableId = license.billable;

    return this.id === billableId;
  }
}

export class Billables<Model extends Billable = Billable> extends Collection<Model> {
  public getModel(attributes: Partial<Attributes<Model>>): Model {
    return new Billable(attributes) as Model;
  }

  public override url(): string {
    return `${this.getHostname()}billing/billables/`;
  }

  public getClassName(): string {
    return 'billables';
  }
}
